import { ApplicationConfig, inject, Injector, LOCALE_ID, PLATFORM_ID, importProvidersFrom } from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { routes } from './app.routes';
import { provideApollo as provideApollo_alias } from 'apollo-angular';
import { ApolloLink, DefaultOptions, FetchResult, InMemoryCache, Observable } from '@apollo/client/core';
import { HttpLink } from 'apollo-angular/http';
import { onError } from '@apollo/client/link/error';
import { authInterceptor } from "./core/auth/auth.interceptor";
import { errorInterceptor } from './core/interceptor/error.interceptor';
import { AuthService } from './core/auth/auth.service';
import Swal from "sweetalert2";
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { MatPaginatorIntl } from "@angular/material/paginator";
import { getCustomPaginatorIntl } from "./core/custom-paginator-intl";
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';
import { NgxMaskConfig, provideNgxMask } from "ngx-mask";
import { environment } from "../environments/environment";

registerLocaleData(localePt);

const maskConfig: Partial<NgxMaskConfig> = {
    validation: true,
};

export const appConfig: ApplicationConfig = {
    providers: [
        importProvidersFrom(MatDialogModule),
        provideRouter(routes),
        provideNativeDateAdapter(),
        // errorInterceptor por fora: o authInterceptor resolve o 401 (refresh +
        // retry) antes de o erro chegar ao alerta genérico. Este interceptor
        // existia desde sempre mas nunca havia sido registrado.
        provideHttpClient(withInterceptors([errorInterceptor, authInterceptor])),
        HttpLink,
        provideApollo_alias(() => {
            const platformId = inject(PLATFORM_ID);
            const httpLink = inject(HttpLink);
            // AuthService não pode ser injetado aqui (ciclo: AuthService → GQL gerado → Apollo);
            // resolvemos lazy via Injector dentro do handler do errorLink.
            const injector = inject(Injector);

            if (!isPlatformBrowser(platformId)) {
                return {
                    cache: new InMemoryCache(),
                };
            }

            const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
                const is401 = graphQLErrors?.some((e: any) =>
                    e?.extensions?.originalError?.statusCode === 401 ||
                    e?.extensions?.code === 'UNAUTHENTICATED'
                );
                const skipRefresh = ['refreshToken', 'loginAdmin', 'logout'].includes(operation.operationName);
                const alreadyRetried = operation.getContext()['retriedAfterRefresh'];

                if (is401 && !skipRefresh && !alreadyRetried) {
                    const authService = injector.get(AuthService);
                    return new Observable<FetchResult>(observer => {
                        authService.refreshSession().then(token => {
                            if (!token) {
                                authService.logout();
                                observer.complete();
                                return;
                            }
                            operation.setContext({ retriedAfterRefresh: true });
                            forward(operation).subscribe({
                                next: value => observer.next(value),
                                error: error => observer.error(error),
                                complete: () => observer.complete(),
                            });
                        });
                    });
                }

                if (graphQLErrors) {
                    graphQLErrors.forEach(({ message, locations, path }) => {
                        // console.error(`[GraphQL Error]: Mensagem: ${message}, Local: ${locations}, Caminho: ${path}`);
                        console.log('message');
                        console.log(message);
                        const elError: any = graphQLErrors[0];
                        if (elError?.extensions?.originalError?.statusCode === 401) {
                            // Refresh já tentado (ou operação de auth): sessão realmente expirada
                            injector.get(AuthService).logout();
                        } else {
                            Swal.fire({
                                title: 'Atenção',
                                html: message,
                                icon: 'warning',
                                confirmButtonText: 'OK',
                            });
                        }
                    });
                }
                if (networkError) {
                    console.error(`[Network Error]: ${networkError}`);
                    Swal.fire({
                        title: 'Erro de Conexão',
                        html: `Não foi possível estabelecer uma conexão segura com o servidor.<br>${networkError.message}`,
                        icon: 'error',
                        confirmButtonText: 'Tentar Novamente',
                    });
                }
                return;
            });

            const defaultOptions: DefaultOptions = {
                watchQuery: {
                    fetchPolicy: 'no-cache',
                    errorPolicy: 'none',
                },
                query: {
                    fetchPolicy: 'no-cache',
                    errorPolicy: 'none',
                },
                mutate: {
                    fetchPolicy: 'no-cache',
                    errorPolicy: 'none',
                },
            };

            return {
                link: ApolloLink.from([
                    errorLink,
                    httpLink.create({ uri: environment.base_graphql, withCredentials: true }),
                ]),
                cache: new InMemoryCache(),
                defaultOptions,
            };
        }),
        provideAnimationsAsync(),
        providePrimeNG({
            theme: {
                preset: Aura,
                options: {
                    // darkModeSelector: '.app-dark',
                    cssLayer: {
                        name: 'primeng',
                        order: 'theme, base, primeng'
                    }
                }
            }
        }),
        { provide: MatPaginatorIntl, useValue: getCustomPaginatorIntl() },
        { provide: LOCALE_ID, useValue: 'pt-BR' },
        { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
        provideNgxMask(maskConfig),
    ],
};
